import { api } from "./api";

// Documented event names — mirror /api/analytics/allowed-events on the backend
export const EVENTS = {
  CLICK_PHONE: "click_phone",
  CLICK_EMAIL: "click_email",
  CLICK_REQUEST_BROCHURE: "click_request_brochure",
  CLICK_REQUEST_VISIT: "click_request_visit",
  CLICK_CHECK_AVAILABILITY: "click_check_availability",
  OPEN_FORM: "open_form",
  SUBMIT_FORM: "submit_form",
  DOWNLOAD_DOCUMENT: "download_document",
  VIEW_FAQ: "view_faq",
  CLICK_AMENITIZ: "click_amenitiz",
  CLICK_UNIVERSE: "click_universe",
  LANGUAGE_SWITCH: "language_switch",
  PAGE_VIEW: "page_view",
  CLICK_CTA: "click_cta",
  CLICK_PARTNER: "click_partner",
  PARTNER_REFERRAL: "partner_referral",
  VAD_ADD_TO_CART: "vad_add_to_cart",
  VAD_CHECKOUT: "vad_checkout",
  GENERATE_LEAD: "generate_lead",
};

// Motifs structurés reconnus comme lead commercial — tout le reste devient "contact".
const LEAD_TYPES = new Set(["mariage", "seminaire", "evenement_prive", "hebergement", "haras", "camion", "contact"]);

/**
 * generate_lead — à appeler UNIQUEMENT après confirmation serveur (HTTP 200)
 * d'une vraie demande commerciale. Aucune PII : lead_type, form_id, page_path.
 */
export function trackGenerateLead({ lead_type, form_id }) {
  try {
    const lt = LEAD_TYPES.has(lead_type) ? lead_type : "contact";
    const page_path = typeof window !== "undefined" ? window.location.pathname : "";
    // Analytics interne (Extranet → Statistiques)
    track(EVENTS.GENERATE_LEAD, { lead_type: lt, form_id, page_path });
    // GA4 — uniquement si le consentement Analytics a chargé gtag
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", "generate_lead", { lead_type: lt, form_id, page_path });
    }
  } catch (_) {
    /* jamais bloquer le parcours utilisateur */
  }
}

/** Récupère (et persiste sur toute la session) les UTM de l'URL courante. */
function readUtms() {
  if (typeof window === "undefined") return {};
  try {
    const url = new URL(window.location.href);
    const utms = {};
    ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"].forEach((k) => {
      const v = url.searchParams.get(k);
      if (v) utms[k] = v.slice(0, 128);
    });
    // Persist first-touch UTMs in sessionStorage so navigation keeps them
    if (Object.keys(utms).length) {
      sessionStorage.setItem("_utms", JSON.stringify(utms));
      return utms;
    }
    const stored = sessionStorage.getItem("_utms");
    return stored ? JSON.parse(stored) : {};
  } catch (_) {
    return {};
  }
}

/**
 * Track a user event. Fires-and-forgets. Safe to call anywhere.
 */
export function track(event_name, payload = {}) {
  try {
    if (typeof window === "undefined") return;
    // Ne tracke pas l'admin/extranet
    const path = window.location.pathname;
    if (path.startsWith("/extranet") || path.startsWith("/mon-evenement")) return;

    const locale = typeof document !== "undefined" ? document.documentElement.lang || "fr" : "fr";
    // referrer utile seulement pour l'entrée initiale (pas les navigations internes)
    const referrer = document.referrer && !document.referrer.startsWith(window.location.origin)
      ? document.referrer.slice(0, 500)
      : null;

    const utms = readUtms();

    api
      .post("/analytics/events", {
        event_name,
        payload,
        url: path,
        referrer,
        locale,
        ...utms,
      })
      .catch(() => {}); // never surface analytics errors
  } catch (_) {
    /* noop */
  }
}

/** Fire a page_view — appelé par le hook usePageView à chaque changement de route. */
let _lastGaPv = { path: null, ts: 0 };
export function trackPageView() {
  track(EVENTS.PAGE_VIEW);
  // Google Analytics 4 (SPA) : page_view à chaque changement de route
  try {
    if (typeof window.gtag === "function") {
      const p = window.location.pathname + window.location.search;
      if (_lastGaPv.path === p && Date.now() - _lastGaPv.ts < 1500) return; // anti-doublon (StrictMode dev)
      _lastGaPv = { path: p, ts: Date.now() };
      window.gtag("event", "page_view", {
        page_path: p,
        page_location: window.location.href,
        page_title: document.title,
      });
    }
  } catch { /* GA absent : ignorer */ }
}

/** Capture ?partenaire={slug} : tracke la provenance et la mémorise pour l'attribution des contacts. */
export function capturePartnerReferral() {
  try {
    const slug = new URL(window.location.href).searchParams.get("partenaire");
    if (!slug) return;
    localStorage.setItem("_partner_ref", slug.slice(0, 255));
    track(EVENTS.PARTNER_REFERRAL, { partner: slug.slice(0, 255) });
  } catch (_) {
    /* noop */
  }
}

/** Slug du partenaire ayant apporté le visiteur (ou null). */
export function getPartnerRef() {
  try {
    return localStorage.getItem("_partner_ref") || null;
  } catch (_) {
    return null;
  }
}
